import React from "react";
import { motion } from "framer-motion";
import { useApp } from "../AppContext";
import { DominicanIsland } from "./DominicanArt";
import DominicanFlag from "./DominicanFlag";

const About = () => {
    const { t, lang, branding } = useApp();

    const aboutTitle =
        (lang === "es" ? branding.about_title_es : branding.about_title_en) || t.about.title;
    const body1 =
        (lang === "es" ? branding.about_body1_es : branding.about_body1_en) || t.about.body1;
    const body2 =
        (lang === "es" ? branding.about_body2_es : branding.about_body2_en) || t.about.body2;

    const stats = [
        {
            value: branding.about_stat1_value || "20+",
            label:
                (lang === "es" ? branding.about_stat1_label_es : branding.about_stat1_label_en) ||
                t.about.stat1,
        },
        {
            value: branding.about_stat2_value || "500+",
            label:
                (lang === "es" ? branding.about_stat2_label_es : branding.about_stat2_label_en) ||
                t.about.stat2,
        },
        {
            value: branding.about_stat3_value || "15+",
            label:
                (lang === "es" ? branding.about_stat3_label_es : branding.about_stat3_label_en) ||
                t.about.stat3,
        },
    ];

    return (
        <section
            id="about"
            data-testid="about-section"
            className="relative py-24 sm:py-32 bg-earth-50"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="lg:col-span-7"
                >
                    <span className="pill-tag mb-5">
                        {branding.decorations_enabled && (
                            <DominicanFlag className="w-5 h-3.5 rounded-sm" />
                        )}
                        {t.about.tag}
                    </span>
                    <h2 className="font-serif font-black text-earth-900 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance">
                        {aboutTitle}
                    </h2>
                    <p className="mt-6 text-lg text-earth-700 leading-relaxed whitespace-pre-line">
                        {body1}
                    </p>
                    <p className="mt-4 text-lg text-earth-700 leading-relaxed whitespace-pre-line">
                        {body2}
                    </p>

                    <div className="mt-10 grid grid-cols-3 gap-6">
                        {stats.map((s, i) => (
                            <div key={i} data-testid={`about-stat-${i}`}>
                                <div
                                    className="font-serif font-black text-4xl sm:text-5xl"
                                    style={{ color: branding.brand_color_primary }}
                                >
                                    {s.value}
                                </div>
                                <div className="text-xs uppercase tracking-wider text-earth-600 font-semibold mt-1">
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="lg:col-span-5"
                >
                    <div className="relative">
                        <div className="aspect-[4/5] rounded-[40px] overflow-hidden shadow-2xl border-8 border-white">
                            <img
                                src="https://images.unsplash.com/photo-1666281005061-97380deccc50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx0YW1hbGVzJTIwcGxhbnRhaW4lMjBsZWF2ZXN8ZW58MHx8fHwxNzc4NDUwNTkxfDA&ixlib=rb-4.1.0&q=85"
                                alt="Tradition"
                                className="w-full h-full object-cover"
                            />
                        </div>
                        {/* Country shape badge — Dominican island only if DR + decorations */}
                        {branding.decorations_enabled && branding.country_code === "DR" && (
                            <div className="absolute -top-6 -right-6 bg-white p-4 rounded-2xl shadow-xl border border-earth-100 rotate-6 hover:rotate-0 transition-transform">
                                <DominicanIsland className="w-28 h-auto" color="#002D62" />
                                <div className="text-[10px] uppercase tracking-wider font-bold text-earth-600 mt-1 text-center">
                                    Santo Domingo
                                </div>
                                <div
                                    className="text-[10px] text-center"
                                    style={{ color: branding.brand_color_primary }}
                                >
                                    → {branding.contact_city || "Montego Bay"}
                                </div>
                            </div>
                        )}
                        <div className="absolute -bottom-8 -left-8 bg-earth-900 text-earth-50 p-7 rounded-3xl max-w-xs shadow-xl">
                            <div className="font-accent text-terracotta-300 text-3xl leading-none mb-1">
                                "Sabor de casa"
                            </div>
                            <p className="text-sm text-earth-200 leading-relaxed">
                                Una receta que viaja desde Santo Domingo hasta tu mesa en Montego Bay.
                            </p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default About;
